/**
 * The People tab's row model, and the mapping from a send outcome to a row's
 * next state. Pure TypeScript, no React, no DOM, no SDK.
 *
 * `locallyInvited` holds the userIds this session has already sent an invite
 * to, and it wins over the server's `relation`, so a row flips the instant
 * the send resolves without waiting for a refetch. `useInviteSend`'s only
 * generated invalidation targets `post:audit:auditList`, an unrelated query
 * key, so a send never invalidates the People tab's search results either.
 */

export type typeInviteRelation = "none" | "member" | "invited" | "blocked";

export type typeInviteSearchHit = {
  userId: string;
  username: string;
  displayName: string;
  avatarUrl?: string | null;
  relation: typeInviteRelation;
};

export type typeInviteRowAction = "invite" | "invited" | "inPool";

export type typeInviteResultRow = {
  userId: string;
  displayName: string;
  handle: string;
  avatarUrl: string | null;
  action: typeInviteRowAction;
};

export function resolveInviteRowAction(
  relation: typeInviteRelation,
  isLocallyInvited: boolean,
): typeInviteRowAction {
  if (isLocallyInvited) return "invited";
  if (relation === "invited") return "invited";
  if (relation === "member") return "inPool";
  if (relation === "none") return "invite";
  // "blocked" maps here only so the function is total. ISSUE-189's handler
  // never emits it (search excludes a blocked fan entirely), so this row is
  // unreachable in production.
  return "inPool";
}

export function buildInviteResultRows(input: {
  hits: readonly typeInviteSearchHit[];
  locallyInvited: ReadonlySet<string>;
}): readonly typeInviteResultRow[] {
  return input.hits.map((hit) => ({
    userId: hit.userId,
    displayName: hit.displayName,
    handle: `@${hit.username}`,
    avatarUrl: hit.avatarUrl ?? null,
    action: resolveInviteRowAction(
      hit.relation,
      input.locallyInvited.has(hit.userId),
    ),
  }));
}

export type typeInviteSendOutcome =
  "sent" | "alreadyMember" | "alreadyInvited" | "blocked";

export function resolveRowStateAfterSend(
  outcome: typeInviteSendOutcome,
): "invited" | "inPool" | "unchanged" {
  if (outcome === "sent") return "invited";
  // The re-send succeeded. The button stays `Invited` and nothing is raised.
  if (outcome === "alreadyInvited") return "invited";
  if (outcome === "alreadyMember") return "inPool";
  // "blocked" is unreachable in practice, kept total.
  return "unchanged";
}
